// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  onAuthStateChanged, 
  signOut, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updatePassword,
  getIdTokenResult
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { validatePassword } from "../utils/passwordUtils";
import { SessionSecurity } from "../utils/sessionSecurity";

const IS_SAAS = process.env.REACT_APP_IS_SAAS === 'true';

const fetchSubscription = async (businessId) => {
  if (!IS_SAAS || !businessId) return null;
  try {
    const snap = await getDoc(doc(db, 'businesses', businessId));
    if (!snap.exists()) return null;
    const sub = snap.data().subscription;
    if (!sub) return null;
    return {
      status: sub.status || 'trialing',
      planId: sub.planId || null,
      billingCycle: sub.billingCycle || null,
      trialEndsAt: sub.trialEndsAt?.toDate?.() ?? null,
      currentPeriodEnd: sub.currentPeriodEnd?.toDate?.() ?? null,
      provider: sub.provider || null,
      manuallyManaged: sub.manuallyManaged || false,
    };
  } catch {
    return null;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      
      try {
        if (currentUser && mounted) {
          const [userDoc, tokenResult] = await Promise.all([
            getDoc(doc(db, "users", currentUser.uid)),
            getIdTokenResult(currentUser, true),
          ]);

          const isSaaS = process.env.REACT_APP_IS_SAAS === 'true';
          const businessId = isSaaS
            ? (tokenResult.claims.businessId || (userDoc.exists() ? userDoc.data().businessId : null) || null)
            : (process.env.REACT_APP_FIREBASE_PROJECT_ID || 'adisyonapp-8a4cb');

          const claimRole = isSaaS ? (tokenResult.claims.role || null) : null;
          const superadmin = tokenResult.claims.superadmin === true;

          const subscription = await fetchSubscription(businessId);

          if (userDoc.exists()) {
            setUser({
              ...currentUser,
              role: claimRole || userDoc.data().role,
              name: userDoc.data().name,
              surname: userDoc.data().surname,
              businessId,
              subscription,
              superadmin,
            });
          } else {
            setUser({ ...currentUser, businessId, role: claimRole, subscription, superadmin });
          }
          SessionSecurity.initSession();
        } else if (mounted) {
          setUser(null);
        }
      } catch (error) {
        console.error("Kullanıcı bilgileri alınırken hata:", error);
        if (mounted) {
          setUser(currentUser);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setAuthInitialized(true);
        }
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const [userDoc, tokenResult] = await Promise.all([
        getDoc(doc(db, "users", userCredential.user.uid)),
        getIdTokenResult(userCredential.user),
      ]);

      const isSaaS = process.env.REACT_APP_IS_SAAS === 'true';
      const businessId = isSaaS
        ? (tokenResult.claims.businessId || (userDoc.exists() ? userDoc.data().businessId : null) || null)
        : (process.env.REACT_APP_FIREBASE_PROJECT_ID || 'adisyonapp-8a4cb');

      const claimRole = isSaaS ? (tokenResult.claims.role || null) : null;
      const superadmin = tokenResult.claims.superadmin === true;

      const subscription = await fetchSubscription(businessId);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser({
          ...userCredential.user,
          role: claimRole || userData.role,
          name: userData.name,
          surname: userData.surname,
          businessId,
          subscription,
          superadmin,
        });
      }

      SessionSecurity.initSession();
      return { user: userCredential.user, superadmin };
    } finally {
      setLoading(false);
    }
  };


  const register = async (email, password) => {
    const { isValid, errors } = validatePassword(password);
    if (!isValid) {
      throw new Error(errors.join('\n'));
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  };

  const logout = async () => {
    await SessionSecurity.terminateSession();
    await signOut(auth);
  };

  const updateUserPassword = async (newPassword) => {
    if (!user) {
      throw new Error('No authenticated user');
    }

    const { isValid, errors } = validatePassword(newPassword);
    if (!isValid) {
      throw new Error(errors.join('\n'));
    }

    await updatePassword(user, newPassword);
  };

  /**
   * SaaS modda setupBusiness callable'dan sonra token'ı yenile ve
   * AuthContext state'ini güncelle. Private modda no-op.
   */
  const refreshToken = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    try {
      const [userDoc, tokenResult] = await Promise.all([
        getDoc(doc(db, "users", currentUser.uid)),
        getIdTokenResult(currentUser, true),
      ]);
      const isSaaS = process.env.REACT_APP_IS_SAAS === 'true';
      const businessId = isSaaS
        ? (tokenResult.claims.businessId || (userDoc.exists() ? userDoc.data().businessId : null) || null)
        : (process.env.REACT_APP_FIREBASE_PROJECT_ID || 'adisyonapp-8a4cb');
      const claimRole = isSaaS ? (tokenResult.claims.role || null) : null;
      const superadmin = tokenResult.claims.superadmin === true;
      const subscription = await fetchSubscription(businessId);

      setUser((prev) => ({
        ...prev,
        role: claimRole || (userDoc.exists() ? userDoc.data().role : prev?.role) || null,
        name: userDoc.exists() ? userDoc.data().name : prev?.name,
        surname: userDoc.exists() ? userDoc.data().surname : prev?.surname,
        businessId,
        subscription,
        superadmin,
      }));
    } catch (error) {
      console.error("Token yenileme hatası:", error);
    }
  };

  const refreshSubscription = async () => {
    if (!user?.businessId) return;
    const subscription = await fetchSubscription(user.businessId);
    setUser((prev) => (prev ? { ...prev, subscription } : prev));
  };

  const value = {
    user,
    loading,
    authInitialized,
    register,
    login,
    logout,
    updateUserPassword,
    refreshToken,
    refreshSubscription,
  };

  return (
    <AuthContext.Provider value={value}>
      {authInitialized && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};